import { useState } from "react";
import {
  Boxes,
  CheckCheck,
  Flag,
  Loader2,
  MessageSquare,
  Phone,
  Plus,
  Trash2,
  TrendingUp,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import type { Interaction, InteractionKind } from "./types";

const kindMeta: Record<
  InteractionKind,
  { label: string; icon: typeof MessageSquare; dotClass: string }
> = {
  create: { label: "创建", icon: Plus, dotClass: "bg-emerald-400" },
  deal: { label: "成交", icon: CheckCheck, dotClass: "bg-emerald-400" },
  price: { label: "价格", icon: TrendingUp, dotClass: "bg-amber-400" },
  quantity: { label: "数量", icon: Boxes, dotClass: "bg-amber-400" },
  status: { label: "状态", icon: Flag, dotClass: "bg-sky-400" },
  call: { label: "电话", icon: Phone, dotClass: "bg-sky-400" },
  meeting: { label: "见面", icon: Users, dotClass: "bg-violet-400" },
  note: {
    label: "沟通",
    icon: MessageSquare,
    dotClass: "bg-muted-foreground/60",
  },
  delete: { label: "删除", icon: Trash2, dotClass: "bg-red-400" },
};

const manualKinds: { value: InteractionKind; label: string }[] = [
  { value: "note", label: "沟通" },
  { value: "call", label: "电话" },
  { value: "meeting", label: "见面" },
  { value: "price", label: "价格变更" },
  { value: "quantity", label: "数量变更" },
  { value: "status", label: "状态变更" },
  { value: "deal", label: "成交" },
];

function toLocalInputValue(date: Date) {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatHappenedAt(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (item: number) => String(item).padStart(2, "0");
  return `${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function InteractionTimeline({
  interactions,
  currentUserId,
  usernameOf,
  onAdd,
  onDelete,
}: {
  interactions: Interaction[];
  currentUserId?: string;
  usernameOf: (userId: string) => string;
  onAdd: (input: {
    content: string;
    happenedAt?: string;
    kind?: InteractionKind;
  }) => Promise<unknown>;
  onDelete: (id: string) => Promise<unknown>;
}) {
  const [content, setContent] = useState("");
  const [kind, setKind] = useState<InteractionKind>("note");
  const [when, setWhen] = useState(() => toLocalInputValue(new Date()));
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const text = content.trim();
    if (!text || busy) return;
    setBusy(true);
    try {
      await onAdd({ content: text, happenedAt: when, kind });
      setContent("");
      toast.success("已记录沟通");
    } catch {
      toast.error("记录保存失败，请重试");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label="跟进时间轴" className="mt-6 border-t pt-5">
      <h3 className="text-xs font-medium text-muted-foreground">跟进时间轴</h3>
      <div className="mt-3 flex flex-col gap-2">
        <Textarea
          aria-label="沟通记录内容"
          className="min-h-16 resize-y"
          value={content}
          onChange={(event) => setContent(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              void submit();
            }
          }}
        />
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="记录类型"
            value={kind}
            onChange={(event) => setKind(event.target.value as InteractionKind)}
            className="h-8 rounded-md border bg-background px-2 text-xs outline-none focus-visible:ring-2 focus-visible:ring-ring/30"
          >
            {manualKinds.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
          {/* 原生 input：Base UI 的 Input 封装对 datetime-local 的 onChange 不可靠 */}
          <input
            type="datetime-local"
            aria-label="发生时间"
            className="h-8 w-48 rounded-md border border-input bg-card px-2 text-xs shadow-none outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30"
            value={when}
            onChange={(event) => setWhen(event.target.value)}
          />
          <Button
            size="sm"
            className="h-8"
            disabled={busy || !content.trim()}
            onClick={() => void submit()}
          >
            {busy ? <Loader2 className="animate-spin" /> : <Plus />}
            记录
          </Button>
        </div>
      </div>

      {interactions.length === 0 ? (
        <p className="mt-5 text-sm text-muted-foreground">暂无跟进记录</p>
      ) : (
        <ol className="mt-5" aria-label="跟进记录列表">
          {interactions.map((interaction, index) => {
            const meta = kindMeta[interaction.kind] ?? kindMeta.note;
            const Icon = meta.icon;
            const author = usernameOf(interaction.created_by);
            const isOwn = Boolean(
              currentUserId && interaction.created_by === currentUserId,
            );
            return (
              <li
                key={interaction.id}
                className="relative flex gap-3 pb-5 last:pb-0"
              >
                {index < interactions.length - 1 && (
                  <span
                    aria-hidden="true"
                    className="absolute top-5 left-[7px] h-full w-px bg-border"
                  />
                )}
                <span
                  className={`z-0 mt-1 grid size-[15px] shrink-0 place-items-center rounded-full ${meta.dotClass}`}
                  title={meta.label}
                >
                  <Icon className="size-2.5 text-background" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <time
                      dateTime={interaction.happened_at}
                      className="text-sm font-semibold tabular-nums"
                    >
                      {formatHappenedAt(interaction.happened_at)}
                    </time>
                    <span className="text-xs text-muted-foreground">
                      {meta.label} · {author}
                      {isOwn ? "（我）" : ""}
                    </span>
                    <button
                      type="button"
                      aria-label="删除这条记录"
                      title="删除"
                      className="ml-auto inline-flex size-6 items-center justify-center rounded text-muted-foreground/50 opacity-0 transition hover:bg-muted hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100"
                      onClick={() => {
                        if (!window.confirm("删除这条跟进记录？")) return;
                        void onDelete(interaction.id).catch(() =>
                          toast.error("删除失败，请重试"),
                        );
                      }}
                    >
                      <Trash2 className="size-3" />
                    </button>
                  </div>
                  <p className="mt-0.5 text-sm leading-6 whitespace-pre-wrap">
                    {interaction.content}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
